import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/hooks';
import { monthlyCutsService } from '@/api/domains/attendance';
import type { LC_AllMonthlyCutoffItemOut } from '@/api/domains/attendance';
import { ROUTE_DEFINITIONS } from '@/routes';


export interface UseMyTeamTardinessDetailNavigationResult {
  handleViewTardinessDetail: () => Promise<void>;
}

// Mirrors findMostRecentCutoff from useMonthSummaryFilters.ts: resolves the most
// recent cutoff by comparing year/month explicitly instead of trusting the response
// order, since that hook already treats the same endpoint's ordering as unreliable.
function findMostRecentCutoffId(cutoffs: LC_AllMonthlyCutoffItemOut[]): number | null {
  if (cutoffs.length === 0) return null;
  const mostRecent = cutoffs.reduce((latest, current) => {
    if (current.year > latest.year) return current;
    if (current.year === latest.year && current.month > latest.month) return current;
    return latest;
  });
  return mostRecent.monthlyCutId;
}

// Data hook for the "Mi equipo" dashboard "Situación de asistencia" section — "Atraso"
// card "Ver detalle" action (ASIS-5405 / REXASIS-004). Resolves the most recently
// generated monthly cutoff of the user's own company and navigates to Aprobar atrasos
// scoped to it; never blocks the click when there are no cutoffs yet.
export function useMyTeamTardinessDetailNavigation(): UseMyTeamTardinessDetailNavigationResult {
  const navigate = useNavigate();
  const companyId = useAppSelector((state) => state.auth.user?.idEmpresa);

  const handleViewTardinessDetail = useCallback(async () => {
    const response = await monthlyCutsService.getAllMonthlyCutoffs(
      undefined,
      companyId ?? undefined,
    );
    const cutoffId = response.success && response.data
      ? findMostRecentCutoffId(response.data)
      : null;

    const query = cutoffId !== null ? `?cutoffId=${cutoffId}` : '';
    void navigate(`${ROUTE_DEFINITIONS.APROBAR_ATRASOS.path}${query}`);
  }, [navigate, companyId]);

  return { handleViewTardinessDetail };
}

export default useMyTeamTardinessDetailNavigation;
